import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Crown, Sparkles, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { azError } from "@/lib/az-errors";
import {
  PROMO_DURATIONS,
  formatAzn,
  promoPrice,
  purchasePromotion,
  useBalance,
  WHATSAPP_LINK,
  type PromoPlan,
} from "@/lib/balance";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/elan-reklam/$id")({
  validateSearch: (search: Record<string, unknown>) => ({
    plan: search['plan'] === "premium" ? ("premium" as const) : ("vip" as const),
    days: [1, 7, 30].includes(Number(search['days'])) ? Number(search['days']) : 7,
  }),
  head: () => ({
    meta: [
      { title: "Elanı önə çıxar — Albazar.az" },
      {
        name: "description",
        content:
          "Elanınızı balansla VIP və ya Premium edin. VIP: 1 gün 1, 7 gün 5, 30 gün 20 AZN. Premium: 1 gün 2, 7 gün 10, 30 gün 30 AZN.",
      },
      { property: "og:title", content: "Elanı önə çıxar — Albazar.az" },
      { property: "og:description", content: "VIP və Premium paketləri balansdan ödəyin." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PromotePage,
});

function PromotePage() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: balance } = useBalance(user?.id);
  const [plan, setPlan] = useState<PromoPlan>(search.plan);
  const [days, setDays] = useState<number>(search.days);

  const { data: listing } = useQuery({
    queryKey: ["listing-promo", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listings")
        .select("id, title, badge, badge_expires_at")
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const price = promoPrice(plan, days);
  const enough = Number(balance ?? 0) >= price;

  const buy = useMutation({
    mutationFn: () => purchasePromotion(id, plan, days),
    onSuccess: () => {
      toast.success(
        `${plan === "vip" ? "VIP" : "Premium"} paket aktivləşdirildi — ${days} gün`,
      );
      void qc.invalidateQueries({ queryKey: ["balance"] });
      void qc.invalidateQueries({ queryKey: ["listing-promo", id] });
      void navigate({ to: "/elan-idare/$id", params: { id } });
    },
    onError: (e: unknown) => toast.error(azError(e)),
  });

  return (
    <div className="container-x max-w-2xl py-6">
      <Link
        to="/elan-idare/$id"
        params={{ id }}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground"
      >
        <ArrowLeft className="size-4" /> Elana qayıt
      </Link>

      <h1 className="mt-4 text-2xl font-extrabold tracking-tight">Elanı önə çıxar</h1>
      {listing?.title && (
        <p className="mt-1 truncate text-sm text-muted-foreground">{listing.title}</p>
      )}

      <div className="mt-4 flex items-center justify-between rounded-2xl border border-border bg-card p-4">
        <span className="flex items-center gap-2 text-sm text-muted-foreground">
          <Wallet className="size-4" /> Balansınız
        </span>
        <span className="text-lg font-extrabold">{formatAzn(balance ?? 0)}</span>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => setPlan("vip")}
          className={cn(
            "rounded-2xl border-2 p-4 text-left transition",
            plan === "vip" ? "border-[hsl(42_92%_52%)] bg-[hsl(42_92%_52%)]/10" : "border-border",
          )}
        >
          <span className="flex items-center gap-2 font-extrabold">
            <Crown className="size-5 text-[hsl(42_92%_52%)]" /> VIP elan
          </span>
          <span className="mt-1 block text-sm text-muted-foreground">
            Siyahılarda üst sıralar və qızılı VIP nişanı.
          </span>
        </button>
        <button
          type="button"
          onClick={() => setPlan("premium")}
          className={cn(
            "rounded-2xl border-2 p-4 text-left transition",
            plan === "premium" ? "border-info bg-info/10" : "border-border",
          )}
        >
          <span className="flex items-center gap-2 font-extrabold">
            <Sparkles className="size-5 text-info" /> Premium elan
          </span>
          <span className="mt-1 block text-sm text-muted-foreground">
            Ən üst sıralar, xüsusi bölmə və statistika.
          </span>
        </button>
      </div>

      <h2 className="mt-6 text-lg font-extrabold">Müddət seç</h2>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        {PROMO_DURATIONS.map((d) => (
          <button
            key={d.days}
            type="button"
            onClick={() => setDays(d.days)}
            className={cn(
              "rounded-2xl border-2 p-4 text-center transition",
              days === d.days ? "border-primary bg-primary/10" : "border-border",
            )}
          >
            <span className="block text-sm text-muted-foreground">{d.label}</span>
            <span className="mt-1 block text-2xl font-black">{promoPrice(plan, d.days)} AZN</span>
          </button>
        ))}
      </div>

      {!enough && (
        <p className="mt-4 rounded-2xl bg-destructive/10 p-3 text-sm text-destructive">
          Balansınız kifayət etmir. Balans artırmaq üçün{" "}
          <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="font-bold underline">
            WhatsApp-a yazın
          </a>
          .
        </p>
      )}

      <Button
        className="mt-5 h-14 w-full rounded-2xl text-base font-bold"
        disabled={!enough || buy.isPending}
        onClick={() => buy.mutate()}
      >
        {price} AZN ödə və {plan === "vip" ? "VIP" : "Premium"} et
      </Button>

      <Link to="/balans" className="mt-3 block text-center text-sm text-muted-foreground underline">
        Balans səhifəsi
      </Link>
    </div>
  );
}